import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Pagination } from '../components/Pagination'
import { DataFreshnessBadge } from '../components/DataFreshnessBadge'
import { useProductData } from '../data'
import type { ErpInventoryRecord, ProductRecord } from '../types'
import { formatTimestamp, integer } from '../utils'

type InventoryStatus = 'stockout' | 'tight' | 'normal' | 'stagnant' | 'dormant'
type StatusFilter = 'all' | InventoryStatus
type StockFilter = 'all' | 'in-stock' | 'zero-stock'
type LinkFilter = 'all' | 'linked' | 'unlinked'
type SortKey = 'stock' | 'available' | 'salesWeek' | 'salesMonth' | 'salesTotal' | 'supportDays' | 'jdMonthUnits'

const TIGHT_SUPPORT_DAYS = 14

const STATUS_META: Record<InventoryStatus, { label: string; tone: string; note: string }> = {
  stockout: { label: '已断货', tone: 'is-stockout', note: '库存为0且近30天仍有销量' },
  tight: { label: '库存紧张', tone: 'is-tight', note: `按近7天销量计算支撑不足${TIGHT_SUPPORT_DAYS}天` },
  normal: { label: '正常', tone: 'is-normal', note: '库存可支撑当前销售节奏' },
  stagnant: { label: '滞销', tone: 'is-stagnant', note: '有库存但近30天无销量' },
  dormant: { label: '无货无销', tone: 'is-dormant', note: '零库存且近30天无销量' },
}

interface InventoryRow {
  record: ErpInventoryRecord
  supportDays: number | null
  status: InventoryStatus
  linkedSkus: ProductRecord[]
  jdMonthUnits: number
}

const rowStatus = (record: ErpInventoryRecord, supportDays: number | null): InventoryStatus => {
  if (record.stock <= 0) return record.salesMonth > 0 ? 'stockout' : 'dormant'
  if (record.salesMonth <= 0) return 'stagnant'
  if (supportDays !== null && supportDays < TIGHT_SUPPORT_DAYS) return 'tight'
  return 'normal'
}

export function InventoryPage() {
  const { data, erpInventory } = useProductData()
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all')
  const [stockFilter, setStockFilter] = useState<StockFilter>('all')
  const [linkFilter, setLinkFilter] = useState<LinkFilter>('all')
  const [sortKey, setSortKey] = useState<SortKey>('salesMonth')
  const [sortDirection, setSortDirection] = useState<'desc' | 'asc'>('desc')
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)

  const skusByCode = useMemo(() => {
    const index = new Map<string, ProductRecord[]>()
    for (const product of data?.products || []) {
      const code = product.merchantSku.trim()
      if (product.level !== 'sku' || !code || code === '--') continue
      const matches = index.get(code) || []
      matches.push(product)
      index.set(code, matches)
    }
    return index
  }, [data])

  const rows = useMemo<InventoryRow[]>(() => (erpInventory?.records || []).map((record) => {
    const supportDays = record.salesWeek > 0 ? record.stock / (record.salesWeek / 7) : null
    const linkedSkus = skusByCode.get(record.productCode) || []
    const jdMonthUnits = linkedSkus.reduce(
      (total, sku) => total + sku.history.reduce((units, row) => units + row[2], 0),
      0,
    )
    return { record, supportDays, status: rowStatus(record, supportDays), linkedSkus, jdMonthUnits }
  }), [erpInventory, skusByCode])

  const statusCounts = useMemo(() => {
    const counts: Record<InventoryStatus, number> = { stockout: 0, tight: 0, normal: 0, stagnant: 0, dormant: 0 }
    for (const row of rows) counts[row.status] += 1
    return counts
  }, [rows])

  const linkedRowCount = useMemo(() => rows.filter((row) => row.linkedSkus.length > 0).length, [rows])
  const stagnantStock = useMemo(
    () => rows.filter((row) => row.status === 'stagnant').reduce((total, row) => total + row.record.stock, 0),
    [rows],
  )

  const filtered = useMemo(() => {
    const needle = search.trim().toLowerCase()
    const result = rows.filter(({ record, status, linkedSkus }) => {
      if (statusFilter !== 'all' && status !== statusFilter) return false
      if (stockFilter === 'in-stock' && record.stock <= 0) return false
      if (stockFilter === 'zero-stock' && record.stock > 0) return false
      if (linkFilter === 'linked' && !linkedSkus.length) return false
      if (linkFilter === 'unlinked' && linkedSkus.length) return false
      if (!needle) return true
      const searchable = [
        record.productCode,
        record.productName,
        record.spec,
        record.barcode,
        ...linkedSkus.flatMap((sku) => [sku.id, sku.spuId, sku.name]),
      ]
      return searchable.some((value) => String(value ?? '').toLowerCase().includes(needle))
    })
    const direction = sortDirection === 'desc' ? -1 : 1
    return result.slice().sort((left, right) => {
      const value = (row: InventoryRow): number => {
        if (sortKey === 'supportDays') {
          if (row.supportDays === null) return sortDirection === 'desc' ? -1 : Number.POSITIVE_INFINITY
          return row.supportDays
        }
        if (sortKey === 'jdMonthUnits') return row.jdMonthUnits
        return row.record[sortKey]
      }
      return (value(left) - value(right)) * direction
    })
  }, [linkFilter, rows, search, sortDirection, sortKey, statusFilter, stockFilter])

  if (!erpInventory) {
    return (
      <div className="empty-state">
        <h1>库存分析</h1>
        <p>尚未导入ERP总库存数据。请先从网店管家导出总库存，再运行 scripts/import_erp_inventory.py。</p>
      </div>
    )
  }

  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize))
  const currentPage = Math.min(page, pageCount)
  const paged = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize)
  const summary = erpInventory.summary

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDirection((direction) => direction === 'desc' ? 'asc' : 'desc')
    else { setSortKey(key); setSortDirection('desc') }
    setPage(1)
  }

  const sortableHeader = (key: SortKey, label: string, title?: string) => {
    const active = sortKey === key
    return (
      <th className="numeric" aria-sort={active ? (sortDirection === 'desc' ? 'descending' : 'ascending') : undefined}>
        <button
          type="button"
          className={`campaign-sort-button${active ? ' is-active' : ''}`}
          title={title || '点击排序：先降序，再升序'}
          onClick={() => toggleSort(key)}
        >
          {label}
          {active
            ? sortDirection === 'desc' ? <ArrowDown aria-hidden="true" /> : <ArrowUp aria-hidden="true" />
            : <ArrowUpDown aria-hidden="true" />}
        </button>
      </th>
    )
  }

  const supportDaysText = (row: InventoryRow) => {
    if (row.supportDays === null) return row.record.stock > 0 ? '—' : '0天'
    if (row.supportDays >= 999) return '999+天'
    return `${Math.round(row.supportDays)}天`
  }

  return (
    <div className="page-stack">
      <header className="page-title-row">
        <div>
          <h1>库存分析</h1>
          <p>
            数据来源：网店管家ERP总库存导出（{formatTimestamp(erpInventory.source.exportedAt)}）。
            库存为自有仓全渠道总库存，销量为全渠道口径，与京东分区库存、商智T+1库存及本站京东成交口径不同，不可混用。
          </p>
        </div>
        <DataFreshnessBadge date={erpInventory.source.exportedAt} label="库存数据截至" mode="on-demand" />
      </header>

      <section className="product-master-summary" aria-label="库存概览">
        <div><span>ERP货品</span><strong>{integer(summary.records)}</strong></div>
        <div><span>有库存货品</span><strong>{integer(summary.inStockCount)}</strong></div>
        <div><span>库存总量</span><strong>{integer(summary.totalStock)}</strong></div>
        <div><span>全渠道近30天销量</span><strong>{integer(summary.salesMonthTotal)}</strong></div>
        <div><span>已断货</span><strong className="inventory-figure-danger">{integer(statusCounts.stockout)}</strong></div>
        <div><span>库存紧张</span><strong className="inventory-figure-warning">{integer(statusCounts.tight)}</strong></div>
        <div><span>滞销货品</span><strong>{integer(statusCounts.stagnant)}</strong></div>
        <div><span>关联京东在售</span><strong>{integer(linkedRowCount)}</strong></div>
      </section>

      <p className="internal-product-source">
        快照：{erpInventory.source.snapshotPath}（SHA256 {erpInventory.source.sha256.slice(0, 8)}…），
        按“货品编号 = 商家SKU”关联京东在售商品，
        {typeof summary.jdSkuMatchedCount === 'number' && typeof summary.jdListedSkuCount === 'number'
          ? `京东在售${integer(summary.jdListedSkuCount)}个SKU中${integer(summary.jdSkuMatchedCount)}个已关联；`
          : ''}
        滞销货品合计占用库存 {integer(stagnantStock)} 件。
        状态口径：已断货=零库存且近30天有销量；库存紧张=按近7天销量支撑不足{TIGHT_SUPPORT_DAYS}天；滞销=有库存但近30天无销量。
      </p>

      <section className="product-master-toolbar internal-product-toolbar">
        <label className="search-control">
          <span className="sr-only">搜索库存货品</span><Search aria-hidden="true" />
          <input
            value={search}
            onChange={(event) => { setSearch(event.target.value); setPage(1) }}
            placeholder="搜索品名、货品编号、条码或京东SKU"
          />
        </label>
        <label>
          <span>库存状态</span>
          <select value={statusFilter} onChange={(event) => { setStatusFilter(event.target.value as StatusFilter); setPage(1) }}>
            <option value="all">全部</option>
            <option value="stockout">已断货（{statusCounts.stockout}）</option>
            <option value="tight">库存紧张（{statusCounts.tight}）</option>
            <option value="normal">正常（{statusCounts.normal}）</option>
            <option value="stagnant">滞销（{statusCounts.stagnant}）</option>
            <option value="dormant">无货无销（{statusCounts.dormant}）</option>
          </select>
        </label>
        <label>
          <span>库存</span>
          <select value={stockFilter} onChange={(event) => { setStockFilter(event.target.value as StockFilter); setPage(1) }}>
            <option value="all">全部</option>
            <option value="in-stock">有库存</option>
            <option value="zero-stock">零库存</option>
          </select>
        </label>
        <label>
          <span>京东关联</span>
          <select value={linkFilter} onChange={(event) => { setLinkFilter(event.target.value as LinkFilter); setPage(1) }}>
            <option value="all">全部</option>
            <option value="linked">已关联京东在售</option>
            <option value="unlinked">未关联</option>
          </select>
        </label>
      </section>

      <div className="table-responsive product-master-table-wrap">
        <table className="product-master-table internal-product-table inventory-table">
          <thead>
            <tr>
              <th>货品编号</th>
              <th>品名</th>
              <th>规格</th>
              {sortableHeader('stock', '库存量')}
              {sortableHeader('available', '可订购')}
              {sortableHeader('salesWeek', '近7天销量', '全渠道近7天销量，点击排序')}
              {sortableHeader('salesMonth', '近30天销量', '全渠道近30天销量，点击排序')}
              {sortableHeader('salesTotal', '总销量', 'ERP累计总销量，点击排序')}
              {sortableHeader('supportDays', '支撑天数', '库存量÷（近7天销量÷7），无近7天销量显示—')}
              {sortableHeader('jdMonthUnits', '京东30天件数', '关联京东SKU在本站滚动数据窗口的成交件数合计')}
              <th>状态</th>
              <th>关联京东SKU</th>
            </tr>
          </thead>
          <tbody>
            {paged.map((row) => (
              <tr key={row.record.productCode}>
                <td>{row.record.productCode}</td>
                <td><strong title={row.record.productName}>{row.record.productName}</strong></td>
                <td>{row.record.spec || '—'}</td>
                <td className="numeric">{integer(row.record.stock)}</td>
                <td className="numeric">{integer(row.record.available)}</td>
                <td className="numeric">{integer(row.record.salesWeek)}</td>
                <td className="numeric">{integer(row.record.salesMonth)}</td>
                <td className="numeric">{integer(row.record.salesTotal)}</td>
                <td className="numeric">{supportDaysText(row)}</td>
                <td className="numeric">{row.linkedSkus.length ? integer(row.jdMonthUnits) : '—'}</td>
                <td>
                  <span className={`inventory-status ${STATUS_META[row.status].tone}`} title={STATUS_META[row.status].note}>
                    {STATUS_META[row.status].label}
                  </span>
                </td>
                <td>
                  {row.linkedSkus.length ? (
                    <span className="internal-linked-skus">
                      <Link to={`/product/sku/${row.linkedSkus[0].id}`}>{row.linkedSkus[0].id}</Link>
                      {row.linkedSkus.length > 1 && <small>+{row.linkedSkus.length - 1}</small>}
                    </span>
                  ) : <span className="internal-unmatched">未关联</span>}
                </td>
              </tr>
            ))}
            {!paged.length && <tr><td colSpan={12}><div className="empty-state">没有符合当前筛选条件的货品。</div></td></tr>}
          </tbody>
        </table>
      </div>

      <div className="product-master-status">
        <span>已筛选 {integer(filtered.length)} 个货品，库存合计 {integer(filtered.reduce((total, row) => total + row.record.stock, 0))} 件</span>
        <span>数据生成于 {formatTimestamp(erpInventory.generatedAt)}</span>
      </div>
      <Pagination page={currentPage} pageCount={pageCount} total={filtered.length} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize} />
    </div>
  )
}
